import { MouseEvent, ReactElement, ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { MajorCardSkeleton } from '@components/MajorCard/MajorCardSkeleton';
import { CoreBox, CoreTooltip, CoreTypography } from '@youscience/core';
import {
  StateSpotlightLocationKey,
  StateSpotlightLocationOptions,
  StatePublicCollegesNotice,
  getStateSpotlightLocationValue,
} from '@components/StateSpotlight';
import { Theme, useMediaQuery } from '@mui/material';
import {
  searchFilters,
  classifications,
  degree,
  MajorListSort,
  MajorSearchFilters,
  SortDirection,
} from '@common-types';

import useAppInitStore from '@store/AppInitStore';

import { CheckboxOption, Option } from '@interfaces';
import { CollegeLocation } from '@interfaces/location';

import { getBreadcrumbs } from '@utils/breadcrumbs';

import useMajorSearchStore, { MajorSearchState } from '@store/MajorSearchStore';
import useUserPreferencesStore from '@store/UserPreferencesStore';
import useHomePageStore from '@store/HomePageStore';

import { useMajors } from '@hooks/useMajors';
import { useStateSpotlight } from '@hooks/useStateSpotlight';
import { UsheIcon } from '@components/UsheIcon';

import { MOBILE_PER_PAGE_OPTIONS } from '@constants';
import { BREADCRUMBS_PAGE_TEXT } from '@constants/breadcrumbs';
import {
  getMajorSortOptions,
  MajorListSortOption,
  STATE_PUBLIC_COLLEGE_FILTER,
  STATE_SPOTLIGHT_MAJOR_SORT,
  getStatePublicCollegesLabel,
  getStatePublicCollegeFilter,
  getRestOfUsFilter,
  REST_OF_US_FILTER,
  REST_OF_US_LABEL,
} from '@constants/stateSpotlight';

import { Pagination } from '@components/Pagination';
import { Container } from '@components/Layout/components/Container';
import { CheckboxGroup, Checkbox } from '@components/Form/Checkbox';
import { FilterAccordion } from '@components/FilterAccordion';
import { MajorCard } from '@components/MajorCard';
import { EmptyStateText } from '@components/EmptyStateText';
import SortBy, { SortByValues } from '@pages/Scholarships/components/SortBy';

import ChipMarkIcon from '@images/chip-mark.svg?react';
import ChipFolderIcon from '@images/chip-folder.svg?react';
import SearchFilesIcon from '@images/search-files.svg?react';

import {
  sxStyles,
  StyledLink,
  StyledMainSectionActions,
  StyledMajorCardsWrap,
  StyledDesktopFiltersWrap,
  StyledBottomPaginationWrap,
} from './MajorList.styles';

import { FilterDrawer } from '@components/FilterDrawer/FilterDrawer';
import { FilterSection } from '@components/FilterSection/FilterSection';
import { ListHeader } from '@components/ListHeader/ListHeader';
import { ExploreModeSwitcher } from '@components/ExploreModeSwitcher/ExploreModeSwitcher';
import { HeaderActions } from '@components/HeaderActions/HeaderActions';

import { usePagination } from '@hooks/usePagination';
import useHasTakenBrainGames from '@hooks/useHasTakenBrainGames';

interface SelectedFiltersProps extends CheckboxOption {
  key: keyof MajorSearchFilters;
  icon?: ReactElement;
}

const MAJOR_CARD_SKELETONS = Array.from({ length: 9 }, (el, index) => ({
  id: index,
  skeleton: MajorCardSkeleton,
}));

interface FormValues {
  cipCodes: string[];
  locales: string[];
  careerFit: boolean;
  careerFavorite: boolean;
  certificationsEarned: boolean;
}

export const renderCheckBoxLabel = (label: string, icon: ReactNode, tooltipTitle?: JSX.Element | string) => {
  return (
    <CoreBox sx={sxStyles.checkBoxLabel}>
      <CoreTypography>{label}</CoreTypography>

      {tooltipTitle && (
        <CoreTooltip title={tooltipTitle}>
          <CoreBox>{icon}</CoreBox>
        </CoreTooltip>
      )}
    </CoreBox>
  );
};

export const MajorList = () => {
  const location: CollegeLocation = useLocation();

  const filters = useMajorSearchStore((state) => state.filters);
  const {
    setSearchFilters,
    removeSearchFilter,
    resetSearchFilters,
    sortByValues,
    setSortByValues,
    setDefaultSortByValues,
    isSortSelected,
  } = useMajorSearchStore((state) => state);

  const hasCareerFilter = !!(filters.careerFit || filters.careerFavorite || filters.certificationsEarned);
  const displayedSortByValues =
    hasCareerFilter && !isSortSelected
      ? { sort: MajorListSort.BEST_FIT, sortDirection: SortDirection.ASC }
      : sortByValues;

  const { spotlight, shouldApplyDefaults, markDefaultsApplied, hasCarriedFilters } = useStateSpotlight();
  const statePublicCollegeFilter = getStatePublicCollegeFilter(filters);

  const removeSearchFilterValue = useMajorSearchStore((state) => state.removeSearchFilterValue);

  const selectedSchoolName = useHomePageStore((state) => state.landingPageSchoolData?.cardInfo?.name);

  const { data: majorsData = { majors: [], totalCount: 0, limit: 0 }, isLoading } = useMajors({
    waitForStateDefaults: !selectedSchoolName,
  });

  const { cipCodes: favoriteMajorsCipCodes } = useUserPreferencesStore((state) => state.majorFavorites);

  const { isLoading: isInitialDataLoading } = useAppInitStore((store) => store);

  const hasTakenBrainGames = useHasTakenBrainGames();

  const isMobileScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const isTabletScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const [selectedValue, setSelectedValue] = useState<SelectedFiltersProps[]>([]);
  const [anchorMobileFilters, setAnchorMobileFilters] = useState<null | HTMLElement>(null);

  const { currentPage, paginationLimit, resetPaginationPage, handlePageSizeChange, handlePaginationChange } =
    usePagination({
      initialLimit: Number(filters.limit),
      initialSkip: Number(filters.skip || 0),
      isMobileScreen,
      isLoading,
      setSearchFilters,
      removeSearchFilter,
    });

  const pageBreadcrumbs = getBreadcrumbs(location.state?.from, BREADCRUMBS_PAGE_TEXT.MAJORS, selectedSchoolName);

  const defaultValues = {
    cipCodes: [],
    awardLevels: [],
    careerFit: false,
    careerFavorite: false,
    certificationsEarned: false,
  };

  const { control, getValues, setValue, reset } = useForm<FormValues>({
    defaultValues,
  });

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  const handleOpenMobileFilters = (event: MouseEvent<HTMLElement>) => {
    setAnchorMobileFilters(event.currentTarget);
  };

  const handleCloseMobileFilters = () => {
    setAnchorMobileFilters(null);
  };

  const resetFilters = () => {
    resetSearchFilters({ limit: paginationLimit });
  };

  const getSelectedFiltersFromState = (isInitialLoading?: boolean) => {
    const options: SelectedFiltersProps[] = [];

    classifications.forEach((cipCode) => {
      if ((filters.cipCodes as string[])?.includes(cipCode.cipCode.toString())) {
        options.push({
          label: cipCode.cipTitle,
          value: cipCode.cipCode.toString(),
          key: searchFilters.areasOfStudy.fieldName,
        });
      }
    });

    (degree as Option[]).forEach((option) => {
      if ((filters.awardLevels as string[])?.includes(option.value)) {
        options.push({ ...option, key: searchFilters.degree.fieldName });
      }
    });

    if (statePublicCollegeFilter && spotlight) {
      options.push({
        label: getStatePublicCollegesLabel(spotlight.stateName),
        value: statePublicCollegeFilter,
        icon: <UsheIcon />,
        key: STATE_PUBLIC_COLLEGE_FILTER as keyof MajorSearchFilters,
      });
    }

    if (getRestOfUsFilter(filters) && spotlight) {
      options.push({
        label: REST_OF_US_LABEL,
        value: 'true',
        key: REST_OF_US_FILTER as keyof MajorSearchFilters,
      });
    }

    if (filters.careerFit) {
      options.push({
        label: searchFilters.personalRecommendation.fieldLabels.isBestFitSoc,
        value: filters.careerFit,
        key: searchFilters.personalRecommendation.fieldNames.isBestFitSoc,
      });
    }

    if (filters.careerFavorite) {
      options.push({
        label: searchFilters.personalRecommendation.fieldLabels.isFavoriteSoc,
        value: filters.careerFavorite,
        key: searchFilters.personalRecommendation.fieldNames.isFavoriteSoc,
      });
    }

    if (filters.certificationsEarned) {
      options.push({
        label: searchFilters.personalRecommendation.fieldLabels.hasCertification,
        value: filters.certificationsEarned,
        key: searchFilters.personalRecommendation.fieldNames.hasCertification,
      });
    }

    if (isInitialLoading) {
      const initialFormValue: { [k: string]: boolean | number | string[] } = defaultValues;

      options.forEach((opt) => {
        if (typeof opt.value === 'boolean') {
          initialFormValue[opt.key] = opt.value;

          return;
        }

        initialFormValue[opt.key] = initialFormValue[opt.key]
          ? (initialFormValue[opt.key] as string[]).concat(opt.value)
          : [opt.value];
      });

      reset(initialFormValue);
    }

    setSelectedValue(options);
  };

  const handleChangeFilters = (filter: keyof MajorSearchState) => (values: string[]) => {
    if (values.length) {
      setSearchFilters({ [filter]: values });
    } else {
      removeSearchFilter(filter);
    }
    resetPaginationPage();
  };

  const handleCheckBox = (filter: keyof MajorSearchState) => (value: boolean) => {
    if (value) {
      setSearchFilters({ [filter]: value });
    } else {
      removeSearchFilter(filter);
    }
    resetPaginationPage();
  };

  const handleChangeStateSpotlightLocation = (key: StateSpotlightLocationKey, checked: boolean) => {
    if (checked && spotlight) {
      setSearchFilters({ [key]: getStateSpotlightLocationValue(key, spotlight) });
    } else {
      removeSearchFilter(key);
    }
    resetPaginationPage();
  };

  const handleChangeSort = (values: SortByValues<MajorListSortOption>) => {
    setSortByValues(values);
    resetPaginationPage();
  };

  const handleDeleteFilterChip = (val: SelectedFiltersProps) => {
    if ((val.key as string) === STATE_PUBLIC_COLLEGE_FILTER || (val.key as string) === REST_OF_US_FILTER) {
      removeSearchFilter(val.key);
      resetPaginationPage();

      return;
    }

    const deletedValue = getValues(val.key as keyof FormValues);

    removeSearchFilterValue(val);

    if (Array.isArray(deletedValue)) {
      const currentValue = deletedValue.filter((value) => value !== val.value);

      setValue(val.key as keyof FormValues, currentValue);
    }

    if (typeof deletedValue === 'boolean') {
      setValue(val.key as keyof FormValues, !deletedValue);
    }

    resetPaginationPage();
  };

  const handleResetFilters = () => {
    reset(defaultValues);

    resetFilters();

    resetPaginationPage();
    setSelectedValue([]);
  };

  useEffect(() => {
    if (isInitialDataLoading) {
      return;
    }

    getSelectedFiltersFromState(true);
  }, [isInitialDataLoading]);

  useEffect(() => {
    const limit = isMobileScreen ? MOBILE_PER_PAGE_OPTIONS[0] : paginationLimit;

    if (filters.limit && filters.limit !== limit) {
      setSearchFilters({ limit });
    }
  }, [isMobileScreen]);

  useEffect(() => {
    getSelectedFiltersFromState();
  }, [filters, spotlight]);

  useEffect(() => {
    if (isInitialDataLoading || selectedSchoolName || !spotlight || !shouldApplyDefaults('majors')) {
      return;
    }

    if (!hasCarriedFilters('majors')) {
      setSearchFilters({ [STATE_PUBLIC_COLLEGE_FILTER]: [spotlight.stateCode] });
    }

    if (!isSortSelected) {
      setDefaultSortByValues(STATE_SPOTLIGHT_MAJOR_SORT);
    }

    markDefaultsApplied('majors');
  }, [isInitialDataLoading, spotlight]);

  const brainGames = () => {
    return (
      <>
        Unlock your superpower with <StyledLink to="https://apps.dev.youscience.com/home">Brain games</StyledLink> to
        discover your aptitudes, interests, and matching best-fit careers and educational opportunities.
      </>
    );
  };

  const renderFilters = (isExpanded?: boolean) => {
    return (
      <FilterSection
        resultCount={majorsData.majors?.length > 0 ? majorsData.totalCount : undefined}
        isLoading={isLoading}
        selectedFilters={selectedValue}
        onDeleteFilter={handleDeleteFilterChip}
        onResetFilters={handleResetFilters}
      >
        {spotlight && !selectedSchoolName && (
          <FilterAccordion data-testid="location" isExpanded={isExpanded} accordionTitle="Location">
            <StateSpotlightLocationOptions
              spotlight={spotlight}
              filters={filters}
              onChange={handleChangeStateSpotlightLocation}
            />
          </FilterAccordion>
        )}

        <FilterAccordion
          data-testid="personalRecommendation"
          isExpanded={isExpanded}
          accordionTitle={searchFilters.personalRecommendation.title}
        >
          <Checkbox
            control={control}
            name={searchFilters.personalRecommendation.fieldNames.isBestFitSoc}
            label={renderCheckBoxLabel(
              searchFilters.personalRecommendation.fieldLabels.isBestFitSoc,
              <ChipMarkIcon />,
              !hasTakenBrainGames
                ? brainGames()
                : 'These are all the majors related to your strong overall fit careers. We use both your aptitude and interest results to calculate the major recommendations.',
            )}
            onChange={handleCheckBox(searchFilters.personalRecommendation.fieldNames.isBestFitSoc)}
            disabled={!hasTakenBrainGames}
          />

          <Checkbox
            control={control}
            name={searchFilters.personalRecommendation.fieldNames.isFavoriteSoc}
            label={renderCheckBoxLabel(
              searchFilters.personalRecommendation.fieldLabels.isFavoriteSoc,
              <ChipFolderIcon />,
              !hasTakenBrainGames ? brainGames() : 'These are all the majors related to careers you have favorited.',
            )}
            onChange={handleCheckBox(searchFilters.personalRecommendation.fieldNames.isFavoriteSoc)}
            disabled={!hasTakenBrainGames}
          />

          {/* TODO - https://youscience.atlassian.net/browse/CC-1988 <Checkbox
            control={control}
            name={searchFilters.personalRecommendation.fieldNames.hasCertification}
            label={renderCheckBoxLabel(
              searchFilters.personalRecommendation.fieldLabels.hasCertification,
              <FilledChipCertificationIcon />,
              'These are all the majors related to the certifications you have earned.',
            )}
            onChange={handleCheckBox(searchFilters.personalRecommendation.fieldNames.hasCertification)}
          /> */}
        </FilterAccordion>

        <FilterAccordion
          data-testid="areasOfStudy"
          isExpanded={isExpanded}
          accordionTitle={searchFilters.areasOfStudy.title}
        >
          <CoreBox id="cipCodesWrap" sx={sxStyles.cipCodesWrap}>
            <CheckboxGroup
              control={control}
              name={searchFilters.areasOfStudy.fieldName}
              options={
                searchFilters.areasOfStudy.filters.sort(
                  (a, b) => a.label?.localeCompare(b.label ?? '') ?? 0,
                ) as Option[]
              }
              onChange={handleChangeFilters(searchFilters.areasOfStudy.fieldName)}
              isShowTooltip
            />
          </CoreBox>
        </FilterAccordion>

        <FilterAccordion data-testid="degree" isExpanded={isExpanded} accordionTitle={searchFilters.degree.title}>
          <CheckboxGroup
            control={control}
            name={searchFilters.degree.fieldName}
            options={searchFilters.degree.filters}
            onChange={handleChangeFilters(searchFilters.degree.fieldName)}
          />
        </FilterAccordion>
      </FilterSection>
    );
  };

  const switcherComponent = !selectedSchoolName && <ExploreModeSwitcher />;
  const headerActions = (
    <HeaderActions
      isTabletScreen={isTabletScreen}
      favoritesCount={favoriteMajorsCipCodes?.length || 0}
      onOpenMobileFilters={handleOpenMobileFilters}
      mode="majors"
    />
  );

  return (
    <CoreBox sx={sxStyles.majorsBg}>
      <ListHeader
        title={selectedSchoolName ? `Explore program at ${selectedSchoolName}` : 'Explore education opportunities'}
        description="Your talents are needed! Put your natural talents to work by checking out these awesome majors and programs."
        breadcrumbs={pageBreadcrumbs}
        actions={headerActions}
        switcherComponent={switcherComponent}
      />

      <Container sx={sxStyles.mainSectionWrap}>
        <StyledDesktopFiltersWrap>{renderFilters()}</StyledDesktopFiltersWrap>

        <CoreBox id="mainSectionContent" sx={sxStyles.mainSectionContent}>
          <StyledMainSectionActions>
            <SortBy<MajorListSortOption>
              sortByValues={displayedSortByValues}
              sortByOptions={getMajorSortOptions(spotlight?.stateName)}
              setSortByFields={handleChangeSort}
              dataTestId="majorSortBySelect"
            />
            <Pagination
              page={currentPage}
              limit={paginationLimit}
              totalCount={majorsData.totalCount}
              isLoading={isLoading}
              onPageSizeChange={handlePageSizeChange}
              onPaginationChange={handlePaginationChange}
            />
          </StyledMainSectionActions>

          {statePublicCollegeFilter && spotlight && !selectedSchoolName && (
            <StatePublicCollegesNotice spotlight={spotlight} />
          )}

          <StyledMajorCardsWrap>
            {(isLoading || isInitialDataLoading) && (
              <>
                {MAJOR_CARD_SKELETONS.map((skeletonCard) => (
                  <MajorCardSkeleton key={skeletonCard.id} />
                ))}
              </>
            )}

            {!isLoading && !isInitialDataLoading && (
              <>
                {majorsData.majors?.map((major) => (
                  <MajorCard componentId="majors-list-card" key={major.cipCode} major={major} />
                ))}

                {majorsData.majors?.length > 0 && (
                  <StyledBottomPaginationWrap>
                    <CoreBox />
                    <Pagination
                      onPageSizeChange={handlePageSizeChange}
                      onPaginationChange={handlePaginationChange}
                      page={currentPage}
                      limit={paginationLimit}
                      totalCount={majorsData.totalCount}
                      isLoading={isLoading}
                    />
                  </StyledBottomPaginationWrap>
                )}

                {majorsData.majors?.length === 0 && (
                  <EmptyStateText
                    icon={<SearchFilesIcon />}
                    title="No results"
                    subtitle="Hmmm. Looks like we couldn't find any matches. Please remove some filters and try again."
                  />
                )}
              </>
            )}
          </StyledMajorCardsWrap>
        </CoreBox>
      </Container>

      <FilterDrawer open={Boolean(anchorMobileFilters)} onClose={handleCloseMobileFilters}>
        {renderFilters(false)}
      </FilterDrawer>
    </CoreBox>
  );
};
